import useApi from '../../hooks/api/useApi';
import useMutate from '../../hooks/api/useMutate';
import api from '../../api/axiosClient';

import { useState, useEffect, useCallback, useRef } from 'react';

// `enabled` false skips the request — a tab the role may not list.
export function useGetBookingList(filters = {}, enabled = true) {
  const { post } = useMutate('/restaurants/booking-list');

  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState({});
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setList([]);
      setPagination({});
      return;
    }
    setIsLoading(true);

    try {
      const payload = {
        location_id: Number(filters.location_id) || null,
        service_period: filters.service_period || null,
        status: filters.status || null,
        date: filters.date || null,
        date_from: filters.date_from || null,
        date_to: filters.date_to || null,
        search: filters.search || null,
        booking_type: filters.booking_type || null,
        page: filters.page || 1,
        per_page: filters.per_page || 15
      };

      const res = await post(payload);

      setList(res?.data || []);
      setPagination(res?.pagination || {});
      setSuccess(res?.success ?? false);
      setMessage(res?.message || '');
    } catch (error) {
      setSuccess(false);
      setMessage(error?.response?.data?.message || 'Failed to load bookings');
      setList([]);
      setPagination({});
    }

    setIsLoading(false);
  }, [JSON.stringify(filters), enabled]); // ← avoids infinite loops

  /** AUTO FETCH on filters change */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** MANUAL REFRESH FUNCTION */
  const refresh = () => fetchData();

  return {
    list,
    pagination,
    success,
    message,
    isLoading,
    refresh
  };
}

export function useAddBooking() {
  const { post } = useMutate('/restaurants/booking-add');
  // useMutate has no busy flag, so this one is kept here. The ref stops a
  // second click (or Enter) sent before the button has re-rendered disabled —
  // each one used to save another copy of the booking. A repeat call gets the
  // request already on its way rather than a new one.
  const [isMutating, setIsMutating] = useState(false);
  const inFlight = useRef(null);

  const addBooking = (payload) => {
    if (inFlight.current) return inFlight.current;
    setIsMutating(true);
    inFlight.current = post(payload).finally(() => {
      inFlight.current = null;
      setIsMutating(false);
    });
    return inFlight.current;
  };

  return { addBooking, isMutating };
}

export function useViewBooking(id) {
  const url = id ? `/restaurants/booking-view/${id}` : null;

  const { data, error, isLoading, refresh } = useApi(url, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false
  });

  return {
    booking: data?.data || null,
    success: data?.success ?? false,
    error,
    isLoading,
    refresh
  };
}

export function useUpdateBooking() {
  const updateBooking = async (id, payload) => {
    if (!id) throw new Error('Booking ID is required');
    const { put } = useMutate(`/restaurants/booking-update/${id}`);
    return await put(payload);
  };

  return { updateBooking };
}

export function useDeleteBooking() {
  const deleteBooking = async (id) => {
    if (!id) throw new Error('ID is required');
    const { delete: del } = useMutate(`/restaurants/booking-delete/${id}`);
    return await del();
  };

  return { deleteBooking };
}

//api for getting service period based on location,date,no of guests
export function useGetServicePeriods() {
  const { post, isMutating } = useMutate('/bookings/service-periods');

  const getServicePeriods = async ({ location_id, date, no_of_guests }) => {
    if (!location_id || !date || !no_of_guests) {
      return {
        success: false,
        servicePeriods: [],
        preferences: [],
        location: null
      };
    }

    try {
      const res = await post({
        location_id,
        date,
        no_of_guests
      });

      return {
        success: res?.success ?? false,

        // Service Periods
        servicePeriods: res?.data?.service_periods || [],

        // Preferences / Facilities
        preferences: res?.data?.facilitiesandpreference || [],

        // Location Info
        location: res?.data?.location || null
      };
    } catch (error) {
      console.error('Service Period API Error:', error);

      return {
        success: false,
        servicePeriods: [],
        preferences: [],
        location: null
      };
    }
  };

  return {
    getServicePeriods,
    isMutating
  };
}

//api for getting time slot based on locationid,date,service period,noofguest
export function useGetAvailableTimeSlots() {
  const { post, isMutating } = useMutate('/bookings/available-slots');

  const getAvailableTimeSlots = async ({ location_id, date, service_period_id, no_of_guests }) => {
    if (!location_id || !date || !service_period_id || !no_of_guests) {
      return {
        success: false,
        timeSlots: [],
        servicePeriod: null,
        location: null
      };
    }

    try {
      const res = await post({
        location_id,
        date,
        service_period_id,
        no_of_guests
      });

      return {
        success: res?.success ?? false,
        timeSlots: res?.data?.time_slots || [],
        location: res?.data?.location || null,
        servicePeriod: res?.data?.service_period || null
      };
    } catch (error) {
      console.error('Time Slot API Error:', error);

      return {
        success: false,
        timeSlots: [],
        location: null,
        servicePeriod: null
      };
    }
  };

  return {
    getAvailableTimeSlots,
    isMutating
  };
}

// UPDATE BOOKING STATUS API
export function useUpdateBookingStatus() {
  const updateStatus = async (id, payload) => {
    if (!id) throw new Error('Booking ID is required');

    // dynamic endpoint just like useUpdateBooking()
    const { post, isMutating } = useMutate(`/booking/update-status/${id}`);

    return await post(payload);
  };

  return { updateStatus };
}

// GET available tables for a booking (lazy — call fetchTables(id) manually)
export function useGetAvailableTables() {
  const fetchTables = async (bookingId) => {
    if (!bookingId) return { success: false, floors: [], hasLayout: true };
    try {
      const res = await api.get(`/restaurants/booking-available-tables/${bookingId}`);
      return {
        success: res.data?.success ?? false,
        floors: res.data?.floors || [],
        // false only when the location has no floor plan or no tables at all.
        // Defaults to true so an older API that omits the flag keeps the
        // existing assign-or-manual behaviour rather than confirming silently.
        hasLayout: res.data?.has_layout !== false
      };
    } catch {
      return { success: false, floors: [], hasLayout: true };
    }
  };
  return { fetchTables };
}

/**
 * Hand a seated table to the till so its bill can be settled.
 *
 * Keyed on the assignment (the sitting), not the table — a table holds several
 * sittings in a day. `assignment_id` comes back on each row of the booking's
 * `tables` array. Resolves with the updated assignment and, when there is one,
 * the open bill.
 */
export async function sendTableToPos(assignmentId) {
  if (!assignmentId) throw new Error('Assignment ID is required');

  const res = await api.post(`/table-layout/assignments/${assignmentId}/send-to-pos`);
  return res.data;
}

// POST assign table or mark manual for a booking
export function useAssignTable() {
  const assignTable = async (bookingId, payload) => {
    if (!bookingId) throw new Error('Booking ID is required');
    const { post } = useMutate(`/restaurants/booking-assign-table/${bookingId}`);
    return await post(payload);
  };
  return { assignTable };
}
